import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Step2 from '../components/wizard/Step2';
import Step3 from '../components/wizard/Step3';

export default function DemoWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  
  // Pre-filled demo data
  const demoData = {
    client_name: 'Acme Restaurant Group',
    claimant_name: 'John Smith',
    business_address: '123 Main Street, New York, NY 10001',
    zone: '',
    business_type: 'Restaurant',
    event_type: 'hurricane',
    revenue_2019: '2500000',
    revenue_2020: '1800000',
    revenue_2021: '2200000',
    revenue_2022: '2600000',
    payroll_2019: '800000',
    payroll_2020: '650000',
    payroll_2021: '750000',
    payroll_2022: '820000'
  };

  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <div className="rounded-xl shadow-2xl p-8" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Step 1: Client Information</h1>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>Pre-filled with demo data</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Client Name</label>
                <input
                  type="text"
                  value={demoData.client_name}
                  readOnly
                  className="w-full px-4 py-3 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Claimant Name</label>
                <input
                  type="text"
                  value={demoData.claimant_name}
                  readOnly
                  className="w-full px-4 py-3 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Business Address</label>
                <input
                  type="text"
                  value={demoData.business_address}
                  readOnly
                  className="w-full px-4 py-3 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
                />
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="mt-8 w-full font-bold py-3 px-6 rounded-lg shadow-lg transition-colors"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Next →
            </button>
          </div>
        );
      
      case 2:
        return (
          <Step2 
            onNext={(data) => {
              // Update demo data with selections
              setStep(3);
            }}
            onBack={() => setStep(1)}
            data={demoData}
          />
        );
      case 3:
        return (
          <Step3 
            onNext={(data) => {
              // Update demo data with financial data
              setStep(4);
            }}
            onBack={() => setStep(2)}
            data={demoData}
          />
        );
      
      case 4:
        return (
          <div className="rounded-xl shadow-2xl p-8" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Step 4: Payroll Data</h1>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>Annual payroll expenses for reconciliation</p>

            <div className="space-y-4">
              {['2019', '2020', '2021', '2022'].map((year) => (
                <div key={year}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Payroll {year}</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 font-semibold" style={{ color: 'var(--color-text-muted)' }}>$</span>
                    <input
                      type="text"
                      value={parseInt(demoData[`payroll_${year}` as keyof typeof demoData] as string).toLocaleString()}
                      readOnly
                      className="w-full pl-8 pr-4 py-3 border rounded-lg"
                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="border-l-4 p-4 mt-6" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' }}>
              <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                <strong>Payroll Analysis:</strong> Payroll reduced by 18.75% in 2020 correlating with revenue decline
              </p>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep(3)}
                className="flex-1 font-bold py-3 px-6 rounded-lg transition-colors"
                style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(5)}
                className="flex-1 font-bold py-3 px-6 rounded-lg shadow-lg transition-colors"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                Next →
              </button>
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="rounded-xl shadow-2xl p-8" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Step 5: Review</h1>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>Verify all information before generating report</p>

            <div className="space-y-4">
              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Client Information</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Client:</strong> {demoData.client_name}</p>
                  <p><strong>Claimant:</strong> {demoData.claimant_name}</p>
                  <p><strong>Address:</strong> {demoData.business_address}</p>
                </div>
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Business Details</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Zone:</strong> {demoData.zone}</p>
                  <p><strong>Business Type:</strong> {demoData.business_type}</p>
                </div>
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Financial Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Total Revenue (4yr):</strong> $9.1M</p>
                  <p><strong>Total Payroll (4yr):</strong> $3.02M</p>
                  <p><strong>2020 Revenue Impact:</strong> -$700K (-28%)</p>
                  <p><strong>2020 Payroll Impact:</strong> -$150K (-19%)</p>
                </div>
              </div>

              <div className="border-l-4 p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
                <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
                  <strong>Estimated BI Claim:</strong> $450,000 based on revenue loss and causation analysis
                </p>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep(4)}
                className="flex-1 font-bold py-3 px-6 rounded-lg transition-colors"
                style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(6)}
                className="flex-1 font-bold py-3 px-6 rounded-lg shadow-lg transition-colors"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                Generate Report →
              </button>
            </div>
          </div>
        );
      
      case 6:
        return (
          <div className="rounded-xl shadow-2xl p-8" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Step 6: Report Generated!</h1>

            <div className="text-center py-12">
              <div className="rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'transparent' }}>
                <svg className="h-8 w-8" style={{ color: 'var(--color-verified)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-verified)' }}>Report Generated Successfully!</h2>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>Report ID: RPT-2025-001</p>

              <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <h3 className="font-semibold text-lg mb-4">Report Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Client</p>
                    <p className="font-semibold">Acme Restaurant Group</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Claim Amount</p>
                    <p className="font-semibold text-xl" style={{ color: 'var(--color-needs-human-text)' }}>$450,000</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Status</p>
                    <p className="font-semibold" style={{ color: 'var(--color-verified)' }}>Completed</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Pages</p>
                    <p className="font-semibold">11-page PDF</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => navigate('/demo')}
                  className="font-bold py-3 px-6 rounded-lg shadow-lg transition-colors"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  View Dashboard
                </button>
                <button
                  onClick={() => setStep(1)}
                  className="font-bold py-3 px-6 rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  Create Another Report
                </button>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="border-l-4 p-4 mb-6 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <div className="flex justify-between items-center">
            <p style={{ color: 'var(--color-needs-human-text)' }}>
              <strong>DEMO WIZARD:</strong> This shows the complete report generation flow with sample data.
            </p>
            <Link
              to="/sign-in"
              className="font-semibold py-2 px-4 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)' }}
            >
              Exit Demo → Sign In
            </Link>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <div
                key={s}
                className="flex items-center justify-center w-10 h-10 rounded-full font-semibold"
                style={
                  s <= step
                    ? { backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)' }
                    : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                }
              >
                {s}
              </div>
            ))}
          </div>
          <div className="h-2 rounded-full" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 6) * 100}%`, backgroundColor: 'var(--color-brand)' }}
            />
          </div>
        </div>

        {renderStep()}
      </div>
    </div>
  );
}
